import { useState, type FormEvent } from 'react';
import {
  createFeatureFlag,
  type FeatureFlagScope,
} from '@/lib/api';
import { describeFeatureFlagError } from '@/lib/feature-flag-errors';

const KEY_PATTERN = /^[a-z][a-z0-9.-]{0,118}[a-z0-9]$/;

export interface FlagPrefill {
  key: string;
  displayName: string;
  description?: string | null;
  scope: FeatureFlagScope;
  defaultEnabled: boolean;
  defaultRolloutPercentage?: number | null;
  /** Kill switch do catálogo: só liga e desliga, então a criação vai sem percentual. */
  isKillSwitch?: boolean;
}

interface Props {
  onClose: () => void;
  onCreated: () => void;
  /** Pre-fill from a catalog descriptor; when set, the key is read-only. */
  prefill?: FlagPrefill;
}

export function FeatureFlagCreateModal({ onClose, onCreated, prefill }: Props) {
  const isKillSwitch = prefill?.isKillSwitch === true;
  const [key, setKey] = useState(prefill?.key ?? '');
  const [displayName, setDisplayName] = useState(prefill?.displayName ?? '');
  const [description, setDescription] = useState(prefill?.description ?? '');
  const [defaultEnabled, setDefaultEnabled] = useState(prefill?.defaultEnabled ?? false);
  const [rolloutPercentage, setRolloutPercentage] = useState<string>(
    !isKillSwitch && prefill?.defaultRolloutPercentage != null ? String(prefill.defaultRolloutPercentage) : '',
  );
  const [scope, setScope] = useState<FeatureFlagScope>(isKillSwitch ? 'Global' : prefill?.scope ?? 'PerTenant');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lockedKey = prefill != null;

  const keyOk = KEY_PATTERN.test(key);
  const rolloutOk = rolloutPercentage === '' || /^([0-9]|[1-9][0-9]|100)$/.test(rolloutPercentage);
  const canSubmit = keyOk && displayName.length > 0 && rolloutOk && !submitting;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await createFeatureFlag({
        key,
        displayName,
        description: description || undefined,
        defaultEnabled,
        defaultRolloutPercentage: isKillSwitch || rolloutPercentage === '' ? null : Number(rolloutPercentage),
        scope,
      });
      onCreated();
    } catch (err) {
      setError(describeFeatureFlagError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="presentation"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 40,
      }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby="ff-create-title"
        style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 520, width: '90%', boxShadow: '0 24px 48px rgba(0,0,0,0.25)' }}>
        <h2 id="ff-create-title" style={{ marginTop: 0, fontSize: 18 }}>
          {lockedKey ? 'Configurar feature flag' : 'Criar feature flag'}
        </h2>
        <form onSubmit={onSubmit}>
          {error && <div className="error-banner">{error}</div>}

          <div className="field">
            <label htmlFor="ff-key">Key (slug)</label>
            <input id="ff-key" value={key} onChange={(e) => setKey(e.target.value.toLowerCase())}
              placeholder="menu.checkout-v2" required readOnly={lockedKey}
              style={lockedKey ? { background: 'var(--color-surface-alt, #F1F5F9)' } : undefined} />
            {lockedKey ? (
              <div style={{ fontSize: 12, color: '#64748B' }}>
                Chave do catálogo — não editável (garante que casa com o consumo no código).
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#64748B' }}>
                Convenção: <code>menu.*</code>, <code>panel.*</code>, <code>platform.*</code>, ou sem ponto (cross-cutting).
              </div>
            )}
            {!lockedKey && !keyOk && key.length > 0 && (
              <div style={{ fontSize: 12, color: '#DC2626' }}>
                Formato inválido. Use lowercase + dígitos + ponto/hífen, começando com letra e terminando com letra/dígito.
              </div>
            )}
          </div>

          <div className="field">
            <label htmlFor="ff-name">Nome</label>
            <input id="ff-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={200} required />
          </div>

          <div className="field">
            <label htmlFor="ff-desc">Descrição (opcional)</label>
            <textarea id="ff-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={2000} />
          </div>

          <div className="field">
            <label htmlFor="ff-scope">Escopo</label>
            <select id="ff-scope" value={scope} onChange={(e) => setScope(e.target.value as FeatureFlagScope)} disabled={isKillSwitch}>
              <option value="PerTenant">PerTenant — overrides permitidos</option>
              <option value="Global">Global — mesmo valor para todos os tenants</option>
            </select>
          </div>

          <div className="field" style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <label>
              <input type="checkbox" checked={defaultEnabled} onChange={(e) => setDefaultEnabled(e.target.checked)} /> Default enabled
            </label>
          </div>

          <div className="field">
            <label htmlFor="ff-rollout">Rollout % (opcional, 0–100)</label>
            <input id="ff-rollout" type="text" value={rolloutPercentage} onChange={(e) => setRolloutPercentage(e.target.value)}
              placeholder="vazio = sem rollout" disabled={isKillSwitch} />
            {isKillSwitch && (
              <div style={{ fontSize: 12, color: '#64748B' }}>kill switch: sem percentual</div>
            )}
            {!rolloutOk && (
              <div style={{ fontSize: 12, color: '#DC2626' }}>Valor inválido.</div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancelar</button>
            <button type="submit" className="btn" disabled={!canSubmit}
              style={{ background: canSubmit ? 'var(--primary, #F97316)' : '#CBD5E1', color: '#fff', padding: '8px 16px', borderRadius: 8 }}>
              {submitting ? 'Criando…' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
